import { Box, Optional } from '@honda-canada/design-system-react';
import React, { useEffect, useRef, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import ProgressBarUI, { ItemUI } from './ProgressBarUI';
import { selectBapUrlQueryString, setActiveProgressBarItem } from '../../core/reducers/ui';
import { useAppDispatch, useAppSelector } from '../../core/utils';
import { useGetCurrentBapPage, useSharedApps } from '../../utils/sitecoreContext';
import { BUILD_AND_PRICE_CONFIGURE_URL } from '../../utils/constants';
import BapSummaryAlert from '../BapSummaryAlert';
import InvalidBuild from './InvalidBuild';
import getIsItemEnabled from './utils/getIsItemEnabled';
import { selectTransmissionFromCurrentVehicle } from '../../core/reducers/vehicles';
import themeStyles from './styles/ProgressBar.styles';
import { useAccessoriesFetch } from '../../core/apiHooks/useAccessoriesFetch';
import useBapGetComparedData from '../../core/apiHooks/saved_build/useBapGetComparedData';
import useBapLoadBuild from './hooks/useBapLoadBuild';
import useBapParamsValidation from './hooks/useBapParamsValidation';
import useHasChargingItem from './useHasChargingItem';
import useCheckConfigureBapSectionAvailability from '../ConfigureBap/hooks/useCheckConfigureBapSectionAvailability';
import useProtectionData from '../ConfigureBap/hooks/useProtectionData';
import useSaveBuild from '../../core/apiHooks/useSaveBuild';
const scrollIntoView = (ref) => {
    var _a;
    (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ inline: 'end', block: 'end' });
};
const AlertsWrapper = themeStyles.apply(Box, 'AlertsWrapper');
const BapProgressBar = ({ fields, variant }) => {
    var _a, _b, _c, _d, _e, _f;
    const dispatch = useAppDispatch();
    const { pathname, hash } = useLocation();
    const { isSummaryPage } = useGetCurrentBapPage();
    const [canRedirectAfterBuildUpdate, setCanRedirectAfterBuildUpdate] = useState(false);
    const currentVehicle = useAppSelector((state) => state.currentVehicle);
    const { redirectToSummary, redirectData, showErrorBuild, userIsBuildOwner } = useBapLoadBuild();
    const { shouldRedirect, redirectUrl, shouldUpdateTemporaryBuild, buildId } = useBapParamsValidation(redirectToSummary, redirectData);
    const { hasConfigurationChanges, isDiffPrice, diffOptions } = useBapGetComparedData();
    const { updateBuild } = useSaveBuild('');
    const itemKeys = (_d = (_c = (_b = (_a = fields === null || fields === void 0 ? void 0 : fields.data) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.items) === null || _c === void 0 ? void 0 : _c.map(item => item === null || item === void 0 ? void 0 : item.name)) !== null && _d !== void 0 ? _d : [];
    const currentTransmission = useAppSelector(selectTransmissionFromCurrentVehicle);
    const hasChargingItem = useHasChargingItem(fields);
    const { data } = useAccessoriesFetch((hasChargingItem && (currentTransmission === null || currentTransmission === void 0 ? void 0 : currentTransmission.detIdentifier)) || '');
    const protectionData = useProtectionData();
    const checkConfigureBapSectionVisibility = useCheckConfigureBapSectionAvailability(protectionData, data);
    const queryParams = useAppSelector(selectBapUrlQueryString);
    const enableSwipe = !!(currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.trimKey);
    const shouldRedirectAfterUpdate = canRedirectAfterBuildUpdate && redirectToSummary;
    const { activeProgressBarItem } = useAppSelector(state => state.ui.ConfigureBap);
    const bapConfigureUrl = useSharedApps(BUILD_AND_PRICE_CONFIGURE_URL);
    const prevPathname = useRef('');
    useEffect(() => {
        var _a, _b, _c, _d;
        // BAP configure page's hash routes are handled in useVisibleSections.ts
        const isBapConfigureHashRoute = pathname === bapConfigureUrl && !!hash && itemKeys.some(key => key.toLocaleLowerCase() === hash.substring(1));
        if (prevPathname.current === pathname || isBapConfigureHashRoute)
            return;
        const matchingSections = (_d = (_c = (_b = (_a = fields === null || fields === void 0 ? void 0 : fields.data) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.items) === null || _c === void 0 ? void 0 : _c.filter((item) => { var _a, _b, _c; return ((_c = (_b = (_a = item === null || item === void 0 ? void 0 : item.progressUrl) === null || _a === void 0 ? void 0 : _a.item) === null || _b === void 0 ? void 0 : _b.value) === null || _c === void 0 ? void 0 : _c.href) === pathname; })) !== null && _d !== void 0 ? _d : [];
        const selectedSection = matchingSections[0];
        if (!selectedSection)
            return;
        dispatch(setActiveProgressBarItem(selectedSection.name));
        return () => {
            prevPathname.current = pathname;
        };
    }, [pathname, hash, activeProgressBarItem, (_f = (_e = fields === null || fields === void 0 ? void 0 : fields.data) === null || _e === void 0 ? void 0 : _e.value) === null || _f === void 0 ? void 0 : _f.items, dispatch, bapConfigureUrl]);
    useEffect(() => {
        if (!shouldUpdateTemporaryBuild || !buildId)
            return;
        updateBuild({
            isTemporaryBuild: true,
            onCompleted() {
                setCanRedirectAfterBuildUpdate(true);
            },
        });
    }, [shouldUpdateTemporaryBuild, buildId]);
    const renderItem = (item, extendedType, ref) => {
        const isItemVisible = checkConfigureBapSectionVisibility(item === null || item === void 0 ? void 0 : item.key.toLowerCase());
        if (!isItemVisible)
            return null;
        const isItemEnabled = getIsItemEnabled(item === null || item === void 0 ? void 0 : item.key, itemKeys, currentVehicle);
        return (React.createElement(ItemUI, { ref: ref, key: item === null || item === void 0 ? void 0 : item.key, item: item, variant: variant, isItemEnabled: isItemEnabled, extendedType: extendedType, queryParams: item.linktype !== 'anchor' ? queryParams : '', isActive: (item === null || item === void 0 ? void 0 : item.key.toLowerCase()) === (activeProgressBarItem === null || activeProgressBarItem === void 0 ? void 0 : activeProgressBarItem.toLowerCase()), scrollIntoViewCallback: scrollIntoView }));
    };
    const canShowAlerts = userIsBuildOwner && isSummaryPage && !showErrorBuild && (hasConfigurationChanges || isDiffPrice);
    return (React.createElement(Box, { "data-testid": "cy-progress_bar" },
        React.createElement(Optional, { when: shouldRedirect || shouldRedirectAfterUpdate },
            React.createElement(Redirect, { to: redirectUrl || '' })),
        React.createElement(Optional, { when: !showErrorBuild },
            React.createElement(ProgressBarUI, { fields: fields, variant: variant, renderItem: renderItem, enableSwipe: enableSwipe, shouldOverrideMobileHeader: true })),
        React.createElement(Optional, { when: canShowAlerts },
            React.createElement(AlertsWrapper, null,
                React.createElement(Optional, { when: hasConfigurationChanges },
                    React.createElement(BapSummaryAlert, { buildItems: diffOptions })),
                React.createElement(Optional, { when: isDiffPrice },
                    React.createElement(BapSummaryAlert, { isPriceAlert: true })))),
        React.createElement(Optional, { when: showErrorBuild },
            React.createElement(InvalidBuild, null))));
};
export default BapProgressBar;
